'use client';

import * as React from 'react';

/**
 * Module-level flag marking that an interactive sign-out is in progress.
 *
 * Sign-out is a multi-step async flow: resolve the Keycloak end-session URL,
 * delete the Better Auth session, then redirect the browser through Keycloak.
 * Between deleting the session and the browser actually leaving, the
 * still-mounted page re-renders against a now-empty session — session-derived
 * queries fire with missing args (e.g. an org id) and throw, or in-flight
 * requests 401 — and the error boundary flashes a full-screen error for the
 * seconds until the redirect lands.
 *
 * This flag lets the app shell drop the page tree for a neutral loader the
 * instant sign-out begins ({@link beginSignOut}), closing that window. It is a
 * one-way latch: once set it stays set until a full page navigation resets the
 * module, because for the rest of this document's life the user is leaving.
 */
let signingOut = false;
const listeners = new Set<() => void>();

/** Marks sign-out as started and notifies subscribers. Idempotent. */
export function beginSignOut(): void {
  if (signingOut) {
    return;
  }
  signingOut = true;
  listeners.forEach((listener) => listener());
}

/** Whether an interactive sign-out is currently in progress. */
export function getIsSigningOut(): boolean {
  return signingOut;
}

function subscribeSigningOut(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Test-only: clears the flag and all subscribers. */
export function resetSignOutState(): void {
  signingOut = false;
  listeners.clear();
}

/**
 * Subscribes a component to the sign-out flag. Returns `true` from the moment
 * {@link beginSignOut} is called until a full page navigation resets the module.
 */
export function useIsSigningOut(): boolean {
  return React.useSyncExternalStore(
    subscribeSigningOut,
    getIsSigningOut,
    () => false,
  );
}
